import { Building2, Calendar, Gift, ImageOff, Percent, Users } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

interface CouponPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  couponName: string;
  couponCode: string;
  thumbnailUrl?: string;
  subject?: string;
  message?: string;
  description?: string;
  isFreeGrant?: boolean;
  discountType?: string;
  discountPercent?: number | string | null;
  discountAmount?: number | string | null;
  licenses?: number | string | null;
  dataLimitGB?: number | string | null;
  durationDays?: number | string | null;
  startsAt?: string | null;
  expiresAt?: string | null;
  usageLimit?: number | string | null;
  limitUsed?: number | string | null;
  organizationIds?: string[];
  disabled?: boolean;
  perUserLimit?: number | string | null;
  minimumOrderValue?: number | string | null;
}

function formatDate(value?: string | null) {
  if (!value) return "?";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "?";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

/**
 * Shows a coupon roughly the way the mobile app renders it to an end user -
 * thumbnail image on top, coupon message below - so an admin can sanity-check
 * both before saving/sharing rather than only seeing raw form fields. Also
 * surfaces the full coupon record (discount/grant, validity, usage, scope,
 * rules) below so this doubles as an at-a-glance summary.
 */
export function CouponPreview({
  open,
  onOpenChange,
  couponName,
  couponCode,
  thumbnailUrl,
  subject,
  message,
  description,
  isFreeGrant,
  discountType,
  discountPercent,
  discountAmount,
  licenses,
  dataLimitGB,
  durationDays,
  startsAt,
  expiresAt,
  usageLimit,
  limitUsed,
  organizationIds,
  disabled,
  perUserLimit,
  minimumOrderValue,
}: CouponPreviewProps) {
  const grantOrDiscount = isFreeGrant
    ? `${licenses ?? "—"} lic · ${dataLimitGB ?? "—"} GB · ${durationDays ?? "—"}d`
    : discountType === "$"
      ? `$${discountAmount ?? 0}`
      : `${discountPercent ?? 0}%`;

  const hasDetails =
    discountType !== undefined ||
    isFreeGrant !== undefined ||
    startsAt !== undefined ||
    expiresAt !== undefined ||
    usageLimit !== undefined ||
    organizationIds !== undefined ||
    disabled !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
          <DialogDescription>
            How this coupon will appear in the Coachable app
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border overflow-hidden">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={couponName}
              className="w-full h-40 object-cover"
            />
          ) : (
            <div className="w-full h-40 flex items-center justify-center bg-muted text-muted-foreground">
              <ImageOff className="h-8 w-8" />
            </div>
          )}

          <div className="p-4 space-y-2 bg-background">
            <p className="text-sm font-semibold">{couponName || "Untitled coupon"}</p>
            {subject?.trim() && <p className="text-sm font-medium">{subject.trim()}</p>}
            {message?.trim() ? (
              <div
                className="text-sm [&_h1]:text-base [&_h1]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: message }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No coupon message set.</p>
            )}
            {couponCode && (
              <p className="text-xs text-muted-foreground">
                Coupon code: <span className="font-mono">{couponCode}</span>
              </p>
            )}
          </div>
        </div>

        {hasDetails && (
          <div className="rounded-md border p-3 space-y-3">
            <p className="flex items-center gap-2 text-sm font-medium">
              {isFreeGrant ? <Gift className="h-4 w-4" /> : <Percent className="h-4 w-4" />}
              Coupon Details
            </p>

            {description?.trim() && (
              <p className="text-sm text-muted-foreground">{description.trim()}</p>
            )}

            <div className="divide-y">
              <DetailRow label={isFreeGrant ? "Grant" : "Discount"} value={grantOrDiscount} />

              {(startsAt !== undefined || expiresAt !== undefined) && (
                <DetailRow
                  label="Validity"
                  value={
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <Calendar className="h-3 w-3" />
                      {formatDate(startsAt)} → {formatDate(expiresAt)}
                    </span>
                  }
                />
              )}

              {usageLimit !== undefined && (
                <DetailRow
                  label="Usage"
                  value={`${limitUsed ?? 0}${usageLimit != null ? ` / ${usageLimit}` : " / ∞"}`}
                />
              )}

              {organizationIds !== undefined && (
                <DetailRow
                  label="Scope"
                  value={
                    <span className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {organizationIds.length === 0 ? (
                        <Badge variant="secondary" className="text-xs">Global</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          {organizationIds.length} orgs
                        </Badge>
                      )}
                    </span>
                  }
                />
              )}

              {disabled !== undefined && (
                <DetailRow
                  label="Status"
                  value={
                    <Badge variant={disabled ? "destructive" : "secondary"} className="text-xs">
                      {disabled ? "Disabled" : "Active"}
                    </Badge>
                  }
                />
              )}

              {(perUserLimit !== undefined || minimumOrderValue !== undefined) && (
                <DetailRow
                  label="Rules"
                  value={
                    <span className="flex items-center gap-1 text-xs">
                      <Users className="h-3 w-3" />
                      {perUserLimit ? `${perUserLimit}/user` : "No per-user limit"}
                      {minimumOrderValue ? ` · min $${minimumOrderValue}` : ""}
                    </span>
                  }
                />
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
